import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Globe, Sparkles, Trophy, Shield, MapPin, Search, X, TrendingUp, Flame, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useExhibition, ExhibitionCountry, ExhibitionState, ExhibitionBooth } from "@/hooks/useExhibition";
import { useExhibitionEvents } from "@/hooks/useExhibitionEvents";
import { useExhibitionFavorites } from "@/hooks/useExhibitionFavorites";
import { useExhibitionEventState } from "@/hooks/useExhibitionEventState";
import { useAuthContext } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
import BackgroundBalloons from "@/components/pop-balloon/BackgroundBalloons";
import { LiveBadge } from "@/components/live-arenas/LiveBadge";
import ExhibitionCountryCard from "@/components/exhibition/ExhibitionCountryCard";
import ExhibitionCityScreen from "@/components/exhibition/ExhibitionCityScreen";
import ExhibitionStateScreen from "@/components/exhibition/ExhibitionStateScreen";
import ExhibitionBoothDirectory from "@/components/exhibition/ExhibitionBoothDirectory";
import ExhibitionBoothPreview from "@/components/exhibition/ExhibitionBoothPreview";
import BoothArenaView from "@/components/exhibition/BoothArenaView";
import ExhibitionGoLiveModal from "@/components/exhibition/ExhibitionGoLiveModal";
import CreateExhibitionBoothModal from "@/components/exhibition/CreateExhibitionBoothModal";
import ExhibitionCountdown from "@/components/exhibition/ExhibitionCountdown";
import ExhibitionEventBanner from "@/components/exhibition/ExhibitionEventBanner";
import ExhibitionLeaderboard from "@/components/exhibition/ExhibitionLeaderboard";
import ExhibitionBadges from "@/components/exhibition/ExhibitionBadges";
import ExhibitionCityLeaderboard from "@/components/exhibition/ExhibitionCityLeaderboard";
import ExhibitionHomeTab from "@/components/exhibition/ExhibitionHomeTab";
import FavoriteButton from "@/components/exhibition/FavoriteButton";
import ExhibitionFavoritesTab from "@/components/exhibition/ExhibitionFavoritesTab";
import ExhibitionHero from "@/components/exhibition/ExhibitionHero";
import ExhibitionAdminControls from "@/components/exhibition/ExhibitionAdminControls";
import ExhibitionPreEventGate from "@/components/exhibition/ExhibitionPreEventGate";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import CountryPicker, { LocationPickerValue } from "@/components/discovery/CountryPicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useGeoData } from "@/hooks/useGeoData";

type Screen = "entry" | "countries" | "states" | "cities" | "booths" | "preview" | "booth";

const LoveExhibition = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const {
    countries,
    states,
    cities,
    booths,
    currentBooth,
    participants,
    remainingRequests,
    loading,
    fetchCountries,
    fetchStates,
    fetchCities,
    fetchBooths,
    fetchParticipants,
    requestToJoin,
    leaveBooth,
    endBooth,
    toggleMute,
    toggleVideo,
    setCurrentBooth,
    pendingRequests,
    fetchPendingRequests,
    acceptRequest,
    rejectRequest,
    createBooth,
  } = useExhibition();

  const {
    currentEvent,
    formattedCountdown,
    mostRequestedBooths,
    topHosts,
    popularExhibitors,
    myBadges,
    loading: eventsLoading,
    fetchCurrentEvent,
  } = useExhibitionEvents();

  const { isAdminOrModerator, loading: roleLoading } = useAdminRole();
  
  const {
    eventData,
    eventState,
    isAdminOverride,
    isPaused,
    isPublished,
    displayTitle,
    displayMessage,
    debugInfo,
    refresh: refetchEventState,
  } = useExhibitionEventState();
  
  const {
    favorites,
    toggleCountryFavorite,
    toggleBoothFavorite,
    isCountryFavorite,
    isBoothFavorite,
    favoriteCountryCodes,
    favoriteBoothIds,
  } = useExhibitionFavorites();

  const [screen, setScreen] = useState<Screen>("entry");
  const [selectedCountry, setSelectedCountry] = useState<ExhibitionCountry | null>(null);
  const [selectedState, setSelectedState] = useState<ExhibitionState | null>(null);
  const [selectedCityId, setSelectedCityId] = useState<string | null>(null);
  const [previewBoothId, setPreviewBoothId] = useState<string | null>(null);
  const [showGoLiveModal, setShowGoLiveModal] = useState(false);
  const [pendingBoothEntry, setPendingBoothEntry] = useState<ExhibitionBooth | null>(null);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showCityLeaderboard, setShowCityLeaderboard] = useState(false);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [countrySearchQuery, setCountrySearchQuery] = useState("");
  const [entryTab, setEntryTab] = useState<"home" | "favorites">("home");
  const [countryViewMode, setCountryViewMode] = useState<"active" | "all">("active");
  
  // Location filter state
  const [showLocationFilter, setShowLocationFilter] = useState(false);
  const [locationFilter, setLocationFilter] = useState<{
    countryCode?: string;
    stateId?: string;
    cityId?: string;
  }>({});
  const [activeMediaStream, setActiveMediaStream] = useState<MediaStream | null>(null);
  
  // Dropdown location selection state
  const { getCountries, getStates, getCities, requiresState } = useGeoData();
  const [dropdownCountry, setDropdownCountry] = useState<string>("");
  const [dropdownState, setDropdownState] = useState<string>("");
  const [dropdownCity, setDropdownCity] = useState<string>("");
  const [dropdownStates, setDropdownStates] = useState<any[]>([]);
  const [dropdownCities, setDropdownCities] = useState<any[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Calculate totals
  const totalBooths = countries.reduce((sum, c) => sum + c.boothCount, 0);
  const totalParticipants = countries.reduce((sum, c) => sum + c.participantCount, 0);

  // Filter and categorize countries
  const { activeCountries, comingSoonCountries, allCountries } = useMemo(() => {
    const query = countrySearchQuery.toLowerCase().trim();
    let result = countries;
    
    // Apply location filter first (from dropdown)
    if (dropdownCountry) {
      result = result.filter(c => c.code === dropdownCountry);
    }
    
    if (showFavoritesOnly) {
      result = result.filter(c => favoriteCountryCodes.includes(c.code));
    }
    
    if (query) {
      result = result.filter(c => 
        c.name.toLowerCase().includes(query) || 
        c.code.toLowerCase().includes(query)
      );
    }
    
    // Sort all countries by engagement (most active first)
    const active = [...result].filter(c => 
      c.participantCount > 0 || c.boothCount > 0
    );
    active.sort((a, b) => {
      if (b.participantCount !== a.participantCount) 
        return b.participantCount - a.participantCount;
      if (b.boothCount !== a.boothCount) 
        return b.boothCount - a.boothCount;
      return a.name.localeCompare(b.name);
    });
    
    // All other countries sorted alphabetically
    const others = result.filter(c => 
      c.participantCount === 0 && c.boothCount === 0
    ).sort((a, b) => a.name.localeCompare(b.name));
    
    return {
      activeCountries: active,
      comingSoonCountries: [], // No more "coming soon" - all countries work
      allCountries: [...active, ...others]
    };
  }, [countries, countrySearchQuery, showFavoritesOnly, favoriteCountryCodes, dropdownCountry]);

  // Determine which countries to display based on view mode
  const displayCountries = useMemo(() => {
    if (countrySearchQuery) {
      // When searching, show all results with badges
      return allCountries;
    }
    return countryViewMode === "active" ? activeCountries : allCountries;
  }, [countryViewMode, activeCountries, allCountries, countrySearchQuery]);

  // Get trending countries (most active) - for "Active" view only
  const trendingCountries = useMemo(() => {
    return [...activeCountries]
      .filter(c => c.participantCount > 0 || c.boothCount > 0)
      .sort((a, b) => (b.participantCount * 2 + b.boothCount) - (a.participantCount * 2 + a.boothCount))
      .slice(0, 4);
  }, [activeCountries]);

  useEffect(() => {
    if (screen === "countries") {
      fetchCountries();
    }
  }, [screen, fetchCountries]);

  // Fetch states when entering states screen
  useEffect(() => {
    if (selectedCountry && screen === "states") {
      fetchStates(selectedCountry.code);
    }
  }, [selectedCountry, screen, fetchStates]);

  // Fetch cities when entering cities screen
  useEffect(() => {
    if (selectedCountry && screen === "cities") {
      // If there's a selected state, fetch cities for that state
      // Otherwise fetch all cities for the country
      fetchCities(selectedCountry.code, selectedState?.id);
    }
  }, [selectedCountry, selectedState, screen, fetchCities]);

  useEffect(() => {
    if (selectedCityId && screen === "booths") {
      fetchBooths(selectedCityId);
    }
  }, [selectedCityId, screen, fetchBooths]);

  useEffect(() => {
    if (currentBooth) {
      fetchParticipants(currentBooth.id);
      if (currentBooth.created_by === user?.id) {
        fetchPendingRequests(currentBooth.id);
      }
    }
  }, [currentBooth, user, fetchParticipants, fetchPendingRequests]);

  const handleEnterExhibition = useCallback(() => {
    setScreen("countries");
  }, []);

  // When selecting a country, check if it has states (like US)
  const handleSelectCountry = useCallback((country: ExhibitionCountry) => {
    setSelectedCountry(country);
    setSelectedState(null); // Reset state selection
    
    // If country has states (like US), go to states screen first
    if (country.hasStates) {
      setScreen("states");
    } else {
      // Otherwise go directly to cities
      setScreen("cities");
    }
  }, []);

  // When selecting a state, go to cities screen
  const handleSelectState = useCallback((stateId: string) => {
    const state = states.find(s => s.id === stateId);
    if (state) {
      setSelectedState(state);
      setScreen("cities");
    }
  }, [states]);

  const handleSelectCity = useCallback((cityId: string) => {
    setSelectedCityId(cityId);
    setScreen("booths");
  }, []);

  const handlePreviewBooth = useCallback((boothId: string) => {
    setPreviewBoothId(boothId);
    setScreen("preview");
  }, []);

  // Handle booth entry - go directly to booth (booth handles pre-join now)
  const handleEnterBooth = useCallback(async (booth: ExhibitionBooth | null) => {
    if (!booth || !user) return;

    try {
      // Register user as participant
      const { error } = await supabase
        .from("arena_participants")
        .upsert({
          arena_id: booth.id,
          user_id: user.id,
          is_speaker: true,
          is_muted: false,
          is_video_on: true,
          left_at: null,
        }, {
          onConflict: 'arena_id,user_id',
        });

      if (error) throw error;

      setCurrentBooth(booth);
      setScreen("booth");
    } catch (error) {
      console.error("Error entering booth:", error);
      toast({
        title: "Failed to enter booth",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  }, [user, setCurrentBooth]);

  // Handle Go Live - join booth with camera stream
  const handleGoLive = useCallback(async (stream: MediaStream) => {
    if (!pendingBoothEntry || !user) return;

    try {
      // Use upsert to handle case where user already joined (e.g. host rejoining their booth)
      const { error } = await supabase
        .from("arena_participants")
        .upsert({
          arena_id: pendingBoothEntry.id,
          user_id: user.id,
          is_speaker: true,
          is_muted: false,
          is_video_on: true,
          left_at: null, // Reset left_at in case they're rejoining
        }, {
          onConflict: 'arena_id,user_id',
        });

      if (error) throw error;

      // Store the stream and set current booth
      setActiveMediaStream(stream);
      setCurrentBooth(pendingBoothEntry);
      setShowGoLiveModal(false);
      setPendingBoothEntry(null);
      setScreen("booth");

      toast({
        title: "You're live! 🎉",
        description: `Welcome to ${pendingBoothEntry.name}`,
      });
    } catch (error) {
      console.error("Error joining booth:", error);
      toast({
        title: "Failed to join booth",
        description: "Please try again.",
        variant: "destructive",
      });
    }
  }, [pendingBoothEntry, user, setCurrentBooth]);

  const handleCreateBooth = useCallback(async (params: Parameters<typeof createBooth>[0]) => {
    const boothId = await createBooth(params);
    if (boothId) {
      // Refresh booths list
      if (selectedCityId) {
        fetchBooths(selectedCityId);
      }
    }
    return boothId;
  }, [createBooth, selectedCityId, fetchBooths]);

  const handleLeaveBooth = useCallback(async () => {
    if (currentBooth) {
      await leaveBooth(currentBooth.id);
      setScreen("booths");
    }
  }, [currentBooth, leaveBooth]);

  const handleBack = useCallback(() => {
    switch (screen) {
      case "countries":
        setScreen("entry");
        break;
      case "states":
        setScreen("countries");
        setSelectedCountry(null);
        setSelectedState(null);
        break;
      case "cities":
        // If we came from states, go back to states
        // Otherwise go back to countries
        if (selectedState) {
          setScreen("states");
          setSelectedState(null);
        } else {
          setScreen("countries");
          setSelectedCountry(null);
        }
        break;
      case "booths":
        setScreen("cities");
        setSelectedCityId(null);
        break;
      case "preview":
        setScreen("booths");
        setPreviewBoothId(null);
        break;
      case "booth":
        handleLeaveBooth();
        break;
      default:
        navigate(-1);
    }
  }, [screen, navigate, handleLeaveBooth, selectedState]);

  const previewBooth = booths.find(b => b.id === previewBoothId);
  const selectedCity = cities.find(c => c.id === selectedCityId);

  // Check if exhibition is accessible (live or admin override)
  const isExhibitionAccessible = eventState === "live" || isAdminOverride;

  // Entry Screen
  if (screen === "entry") {
    // Wait for role check to complete before showing gate
    // This prevents flashing the gate to admins while their role is being verified
    if (roleLoading && !isExhibitionAccessible) {
      return (
        <div className="min-h-screen sky-gradient relative overflow-hidden">
          <BackgroundBalloons />
          <div className="relative z-10 flex flex-col items-center justify-center min-h-screen pb-20">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="mt-4 text-muted-foreground">Checking access...</p>
          </div>
        </div>
      );
    }

    // Show pre-event gate when locked/closed/no-event (unless admin override or admin user)
    if (!isExhibitionAccessible && !isAdminOrModerator) {
      return (
        <div className="min-h-screen sky-gradient relative overflow-hidden">
          <BackgroundBalloons />
          
          <div className="relative z-10 flex flex-col min-h-screen pb-20">
            {/* Header */}
            <div className="px-4 pt-4 pb-2">
              <div className="flex items-center justify-between">
                <button onClick={() => navigate(-1)} className="p-2 -ml-2">
                  <ArrowLeft className="w-6 h-6 text-foreground" />
                </button>
                <div className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-primary" />
                  <span className="font-bold text-foreground">Love Exhibition</span>
                </div>
                <div className="w-10" />
              </div>
            </div>
            
            <ExhibitionPreEventGate
              eventName={eventData?.name ?? null}
              eventDescription={eventData?.description ?? null}
              startsAt={eventData?.starts_at ?? null}
              countdown={eventState === "locked" ? {
                days: Math.floor((eventData?.countdown_seconds ?? 0) / 86400),
                hours: Math.floor(((eventData?.countdown_seconds ?? 0) % 86400) / 3600),
                minutes: Math.floor(((eventData?.countdown_seconds ?? 0) % 3600) / 60),
                seconds: (eventData?.countdown_seconds ?? 0) % 60,
              } : null}
              eventState={eventState as "locked" | "closed" | "no-event"}
              displayTitle={displayTitle}
              displayMessage={displayMessage}
            />
          </div>
        </div>
      );
    }

    // Admin viewing locked exhibition - show preview with admin badge
    const showAdminBanner = isAdminOrModerator && !isExhibitionAccessible;

    return (
      <div className="min-h-screen sky-gradient relative overflow-hidden">
        <BackgroundBalloons />
        
        <div className="relative z-10 flex flex-col min-h-screen pb-20 overflow-y-auto">
          {/* Admin Preview Banner */}
          {showAdminBanner && (
            <div className="mx-4 mt-4 p-3 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Admin Preview Mode</p>
                <p className="text-xs text-amber-700 dark:text-amber-300">Exhibition is {eventState}. Use admin controls to go live.</p>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-center justify-between">
              <button onClick={() => navigate(-1)} className="p-2 -ml-2">
                <ArrowLeft className="w-6 h-6 text-foreground" />
              </button>
              <div className="flex items-center gap-2">
                {isAdminOrModerator && (
                  <button 
                    onClick={() => navigate("/admin")}
                    className="p-2 rounded-full bg-primary/20 text-primary"
                    title="Admin Panel"
                  >
                    <Shield className="w-5 h-5" />
                  </button>
                )}
                <button 
                  onClick={() => setShowCityLeaderboard(!showCityLeaderboard)}
                  className="p-2 rounded-full bg-blue-500/20 text-blue-600"
                  title="City Leaderboard"
                >
                  <MapPin className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => setShowLeaderboard(!showLeaderboard)}
                  className="p-2 rounded-full bg-amber-500/20 text-amber-600"
                  title="Leaderboards"
                >
                  <Trophy className="w-5 h-5" />
                </button>
                <LiveBadge variant="glow" />
              </div>
            </div>
          </div>

          {/* Event Banner or Hero */}
          {currentEvent ? (
            <ExhibitionEventBanner
              event={currentEvent}
              countdown={formattedCountdown}
              participantCount={totalParticipants}
              boothCount={totalBooths}
              onEnter={handleEnterExhibition}
            />
          ) : (
            <ExhibitionHero
              countriesCount={countries.length || 10}
              totalParticipants={totalParticipants}
              totalBooths={totalBooths}
              remainingRequests={remainingRequests}
              onEnter={handleEnterExhibition}
            />
          )}

          {/* Countdown for upcoming event */}
          {currentEvent && !currentEvent.is_live && formattedCountdown && (
            <ExhibitionCountdown
              eventName={currentEvent.name}
              eventDescription={currentEvent.description}
              startsAt={currentEvent.starts_at}
              countdown={formattedCountdown}
              isLive={currentEvent.is_live}
            />
          )}

          {/* Tabs for Home and Favorites */}
          <Tabs value={entryTab} onValueChange={(v) => setEntryTab(v as "home" | "favorites")} className="mt-4">
            <TabsList className="mx-4 grid grid-cols-2 bg-white/50 backdrop-blur-sm">
              <TabsTrigger value="home" className="data-[state=active]:bg-white">
                <Globe className="w-4 h-4 mr-2" />
                Home
              </TabsTrigger>
              <TabsTrigger value="favorites" className="data-[state=active]:bg-white">
                <Star className="w-4 h-4 mr-2" />
                Favorites
                {(favoriteCountryCodes.length + favoriteBoothIds.length) > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-amber-500 text-white">
                    {favoriteCountryCodes.length + favoriteBoothIds.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="home" className="mt-0">
              <ExhibitionHomeTab
                totalBooths={totalBooths}
                totalParticipants={totalParticipants}
                countriesCount={countries.length}
                remainingRequests={remainingRequests}
                onEnterExhibition={handleEnterExhibition}
                onPreviewBooth={(boothId) => {
                  setPreviewBoothId(boothId);
                  setScreen("preview");
                }}
                onNavigateToCity={(cityId, countryCode) => {
                  const country = countries.find(c => c.code === countryCode);
                  if (country) {
                    setSelectedCountry(country);
                    setSelectedCityId(cityId);
                    setScreen("booths");
                  }
                }}
              />

              {/* My Badges (compact) */}
              {myBadges.length > 0 && (
                <div className="mx-4 mt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Trophy className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-semibold text-foreground">Your Badges</span>
                  </div>
                  <ExhibitionBadges badges={myBadges} compact />
                </div>
              )}

              {/* City Leaderboard section */}
              {showCityLeaderboard && (
                <div className="mx-4 mt-4 rounded-xl bg-white/50 dark:bg-gray-900/50 backdrop-blur-xl border border-white/60 dark:border-gray-700/60 p-4">
                  <ExhibitionCityLeaderboard compact />
                </div>
              )}

              {showLeaderboard && (
                <ExhibitionLeaderboard
                  mostRequestedBooths={mostRequestedBooths}
                  topHosts={topHosts}
                  popularExhibitors={popularExhibitors}
                  loading={eventsLoading}
                />
              )}
            </TabsContent>

            <TabsContent value="favorites" className="mt-0">
              <ExhibitionFavoritesTab
                favoriteCountryCodes={favoriteCountryCodes}
                favoriteBoothIds={favoriteBoothIds}
                countries={countries}
                onSelectCountry={(country) => {
                  setSelectedCountry(country);
                  setScreen("cities");
                }}
                onPreviewBooth={(boothId) => {
                  setPreviewBoothId(boothId);
                  setScreen("preview");
                }}
                onJoinBooth={(boothId) => {
                  // Find booth in loaded booths or fetch it
                  const booth = booths.find(b => b.id === boothId);
                  if (booth) {
                    handleEnterBooth(booth);
                  } else {
                    // If booth not loaded, go to preview first
                    setPreviewBoothId(boothId);
                    setScreen("preview");
                  }
                }}
                onToggleCountryFavorite={toggleCountryFavorite}
                onToggleBoothFavorite={toggleBoothFavorite}
            />
            </TabsContent>
          </Tabs>
        </div>

        {/* Admin Controls - visible for admins */}
        {isAdminOrModerator && (
          <ExhibitionAdminControls
            eventId={eventData?.id ?? null}
            eventState={eventState}
            isAdminOverride={isAdminOverride}
            isPaused={isPaused}
            debugInfo={debugInfo ?? null}
            onStateChange={() => {
              refetchEventState();
              fetchCurrentEvent();
            }}
          />
        )}
      </div>
    );
  }

  // Countries Screen
  if (screen === "countries") {
    return (
      <div className="min-h-screen sky-gradient relative overflow-hidden">
        <BackgroundBalloons />
        
        <div className="relative z-10 flex flex-col min-h-screen pb-20">
          {/* Header */}
          <div className="px-4 pt-4 pb-2">
            <div className="flex items-center justify-between">
              <button onClick={handleBack} className="p-2 -ml-2">
                <ArrowLeft className="w-6 h-6 text-foreground" />
              </button>
              <LiveBadge variant="glow" />
            </div>
            
            <div className="text-center mt-2">
              <h1 className="text-3xl font-bold text-foreground">Country Pavilions</h1>
              <p className="text-muted-foreground text-sm">Select a country to explore</p>
            </div>
          </div>

          {/* Remaining requests & Favorites toggle */}
          <div className="mx-4 mt-4 flex gap-2">
            <div className="flex-1 p-3 rounded-xl backdrop-blur-xl bg-white/50 border border-white/60">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Daily Join Requests</span>
                <span className="text-sm font-bold text-pop">{remainingRequests} / 5 left</span>
              </div>
            </div>
            {favoriteCountryCodes.length > 0 && (
              <button
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                className={`p-3 rounded-xl backdrop-blur-xl border transition-all ${
                  showFavoritesOnly
                    ? "bg-amber-500 text-white border-amber-500"
                    : "bg-white/50 border-white/60 text-muted-foreground hover:text-amber-500"
                }`}
                title="Show favorites only"
              >
                <Star className={`w-5 h-5 ${showFavoritesOnly ? "fill-white" : ""}`} />
              </button>
            )}
          </div>

          {/* Active/All Segmented Control */}
          <div className="mx-4 mt-4">
            <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-muted/50 backdrop-blur-xl border border-border/50">
              <button
                onClick={() => setCountryViewMode("active")}
                className={cn(
                  "px-4 py-2 rounded-md text-sm font-medium transition-all",
                  countryViewMode === "active"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                Active {activeCountries.length > 0 && `(${activeCountries.length})`}
              </button>
              <button
                onClick={() => setCountryViewMode("all")}
                className={cn(
                  "px-4 py-2 rounded-md text-sm font-medium transition-all",
                  countryViewMode === "all"
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                All {allCountries.length > 0 && `(${allCountries.length})`}
              </button>
            </div>
          </div>

          {/* Location Dropdowns */}
          <div className="mx-4 mt-4 space-y-3">
            <div className="flex gap-2 flex-wrap">
              {/* Country Dropdown */}
              <Select
                value={dropdownCountry}
                onValueChange={(value) => {
                  setDropdownCountry(value);
                  setLocationFilter({ countryCode: value || undefined });
                  // Navigate to country if selected
                  if (value) {
                    const country = countries.find(c => c.code === value);
                    if (country) {
                      handleSelectCountry(country);
                    }
                  }
                }}
              >
                <SelectTrigger className="flex-1 min-w-[150px] h-11 rounded-xl bg-white/60 backdrop-blur-sm border-white/50">
                  <SelectValue placeholder="Select Country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country) => (
                    <SelectItem key={country.code} value={country.code}>
                      {country.flag} {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* State/Province Dropdown - Only show if country has states */}
              {dropdownCountry && requiresState(dropdownCountry) && (
                <Select
                  value={dropdownState}
                  onValueChange={(value) => {
                    setDropdownState(value);
                    setLocationFilter({
                      countryCode: dropdownCountry,
                      stateId: value || undefined,
                    });
                  }}
                  disabled={loadingStates || dropdownStates.length === 0}
                >
                  <SelectTrigger className="flex-1 min-w-[150px] h-11 rounded-xl bg-white/60 backdrop-blur-sm border-white/50">
                    <SelectValue placeholder={dropdownCountry === "CA" ? "Select Province" : "Select State"} />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingStates ? (
                      <SelectItem value="loading" disabled>Loading...</SelectItem>
                    ) : dropdownStates.length === 0 ? (
                      <SelectItem value="none" disabled>No states available</SelectItem>
                    ) : (
                      dropdownStates.map((state) => (
                        <SelectItem key={state.id} value={state.id}>
                          {state.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}

              {/* City Dropdown - Show if country selected (and state if required) */}
              {dropdownCountry && (!requiresState(dropdownCountry) || dropdownState) && (
                <Select
                  value={dropdownCity}
                  onValueChange={(value) => {
                    setDropdownCity(value);
                    setLocationFilter({
                      countryCode: dropdownCountry,
                      stateId: dropdownState || undefined,
                      cityId: value || undefined,
                    });
                  }}
                  disabled={loadingCities || dropdownCities.length === 0}
                >
                  <SelectTrigger className="flex-1 min-w-[150px] h-11 rounded-xl bg-white/60 backdrop-blur-sm border-white/50">
                    <SelectValue placeholder="Select City" />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingCities ? (
                      <SelectItem value="loading" disabled>Loading cities...</SelectItem>
                    ) : dropdownCities.length === 0 ? (
                      <SelectItem value="none" disabled>No cities available</SelectItem>
                    ) : (
                      dropdownCities.map((city) => (
                        <SelectItem key={city.id} value={city.id}>
                          {city.name}
                          {city.state_name && ` (${city.state_name})`}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Clear filter button */}
            {(dropdownCountry || dropdownState || dropdownCity) && (
              <button
                onClick={() => {
                  setDropdownCountry("");
                  setDropdownState("");
                  setDropdownCity("");
                  setLocationFilter({});
                }}
                className="text-sm text-primary hover:underline flex items-center gap-1"
              >
                <X className="w-3 h-3" />
                Clear location filter
              </button>
            )}
          </div>

          {/* Search Input */}
          {countries.length > 5 && (
            <div className="mx-4 mt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search countries..."
                  value={countrySearchQuery}
                  onChange={(e) => setCountrySearchQuery(e.target.value)}
                  className="pl-9 pr-9 h-11 rounded-xl bg-white/60 backdrop-blur-sm border-white/50 focus-visible:ring-1 focus-visible:ring-primary"
                />
                {countrySearchQuery && (
                  <button
                    onClick={() => setCountrySearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Countries list */}
          <div className="flex-1 overflow-y-auto px-4 mt-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-10 h-10 rounded-full border-4 border-pop border-t-transparent animate-spin" />
              </div>
            ) : displayCountries.length === 0 && countrySearchQuery ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">No countries found matching "{countrySearchQuery}"</p>
                <button 
                  onClick={() => setCountrySearchQuery("")}
                  className="mt-2 text-sm text-primary hover:underline"
                >
                  Clear search
                </button>
              </div>
            ) : displayCountries.length === 0 && countryViewMode === "active" ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground mb-2">No active countries at the moment</p>
                <button 
                  onClick={() => setCountryViewMode("all")}
                  className="text-sm text-primary hover:underline"
                >
                  View all countries
                </button>
              </div>
            ) : (
              <div className="space-y-4 pb-8">
                {/* Show trending if in active mode and not searching */}
                {trendingCountries.length > 0 && !countrySearchQuery && countryViewMode === "active" && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white">
                        <TrendingUp className="w-4 h-4" />
                        <span className="text-sm font-bold">Trending</span>
                        <Flame className="w-3 h-3 animate-pulse" />
                      </div>
                      <span className="text-xs text-muted-foreground">Most active</span>
                    </div>
                    
                    <div className="space-y-3">
                      {trendingCountries.map((country, index) => (
                        <div key={`trending-${country.code}`} className="relative">
                          <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold shadow-lg z-10" style={{
                            background: index === 0 ? '#f59e0b' : index === 1 ? '#9ca3af' : index === 2 ? '#b45309' : '#fff',
                            color: index < 3 ? '#fff' : '#000'
                          }}>
                            {index + 1}
                          </div>
                          <div className="ml-4">
                            <ExhibitionCountryCard
                              country={country}
                              isFavorite={isCountryFavorite(country.code)}
                              onToggleFavorite={() => toggleCountryFavorite(country.code)}
                              onClick={() => handleSelectCountry(country)}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Active Countries Section (when in "all" mode) */}
                {countryViewMode === "all" && activeCountries.length > 0 && !countrySearchQuery && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">Active Countries</span>
                      <span className="text-xs text-muted-foreground">({activeCountries.length})</span>
                    </div>
                    <div className="space-y-3">
                      {activeCountries.map((country) => (
                        <ExhibitionCountryCard
                          key={country.code}
                          country={country}
                          isFavorite={isCountryFavorite(country.code)}
                          onToggleFavorite={() => toggleCountryFavorite(country.code)}
                          onClick={() => handleSelectCountry(country)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* When searching, show all results */}
                {countrySearchQuery && displayCountries.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">Search Results</span>
                      <span className="text-xs text-muted-foreground">({displayCountries.length})</span>
                    </div>
                    <div className="space-y-3">
                      {displayCountries.map((country) => (
                        <ExhibitionCountryCard
                          key={country.code}
                          country={country}
                          isFavorite={isCountryFavorite(country.code)}
                          onToggleFavorite={() => toggleCountryFavorite(country.code)}
                          onClick={() => handleSelectCountry(country)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* When in active mode and not searching - show remaining active countries */}
                {countryViewMode === "active" && !countrySearchQuery && activeCountries.length > trendingCountries.length && (
                  <div className="space-y-3">
                    {trendingCountries.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">All Active</span>
                        <span className="text-xs text-muted-foreground">({activeCountries.length - trendingCountries.length})</span>
                      </div>
                    )}
                    <div className="space-y-3">
                      {activeCountries
                        .filter(c => !trendingCountries.find(t => t.code === c.code))
                        .map((country) => (
                          <ExhibitionCountryCard
                            key={country.code}
                            country={country}
                            isFavorite={isCountryFavorite(country.code)}
                            onToggleFavorite={() => toggleCountryFavorite(country.code)}
                            onClick={() => handleSelectCountry(country)}
                          />
                        ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // States Screen (for countries like US that have states)
  if (screen === "states" && selectedCountry) {
    return (
      <ExhibitionStateScreen
        country={selectedCountry}
        states={states}
        loading={loading}
        remainingRequests={remainingRequests}
        onBack={handleBack}
        onSelectState={handleSelectState}
      />
    );
  }

  // Cities Screen
  if (screen === "cities" && selectedCountry) {
    return (
      <ExhibitionCityScreen
        country={selectedCountry}
        cities={cities}
        loading={loading}
        remainingRequests={remainingRequests}
        onBack={handleBack}
        onSelectCity={handleSelectCity}
        selectedState={selectedState}
      />
    );
  }

  // Booths Directory
  if (screen === "booths" && selectedCity && selectedCountry) {
    return (
      <>
        <ExhibitionBoothDirectory
          city={selectedCity}
          country={selectedCountry}
          booths={booths}
          loading={loading}
          remainingRequests={remainingRequests}
          favoriteBoothIds={favoriteBoothIds}
          onBack={handleBack}
          onPreviewBooth={handlePreviewBooth}
          onEnterBooth={handleEnterBooth}
          onToggleBoothFavorite={toggleBoothFavorite}
          createBoothModal={
            <CreateExhibitionBoothModal
              cityId={selectedCity.id}
              cityName={selectedCity.name}
              countryCode={selectedCountry.code}
              countryName={selectedCountry.name}
              onCreate={handleCreateBooth}
            />
          }
        />
        
        {/* Go Live Modal - triggered when entering booth from directory */}
        <ExhibitionGoLiveModal
          isOpen={showGoLiveModal}
          onClose={() => {
            setShowGoLiveModal(false);
            setPendingBoothEntry(null);
          }}
          onGoLive={handleGoLive}
          boothName={pendingBoothEntry?.name || "Booth"}
          cityName={selectedCity?.name}
          countryFlag={selectedCountry?.flag}
          isHost={pendingBoothEntry?.created_by === user?.id}
        />
      </>
    );
  }

  // Booth Preview
  if (screen === "preview" && previewBooth) {
    return (
      <>
        <ExhibitionBoothPreview
          booth={previewBooth}
          city={selectedCity}
          country={selectedCountry}
          currentUserId={user?.id}
          onBack={handleBack}
          onEnterBooth={() => handleEnterBooth(previewBooth)}
        />
        
        {/* Go Live Modal - triggered when entering booth */}
        <ExhibitionGoLiveModal
          isOpen={showGoLiveModal}
          onClose={() => {
            setShowGoLiveModal(false);
            setPendingBoothEntry(null);
          }}
          onGoLive={handleGoLive}
          boothName={pendingBoothEntry?.name || previewBooth.name}
          cityName={selectedCity?.name}
          countryFlag={selectedCountry?.flag}
          isHost={pendingBoothEntry?.created_by === user?.id}
        />
      </>
    );
  }

  // Inside Booth - Enhanced Booth Arena View
  if (screen === "booth" && currentBooth) {
    return (
      <BoothArenaView
        booth={currentBooth}
        participants={participants}
        currentUserId={user?.id}
        cityName={selectedCity?.name}
        countryName={selectedCountry?.name}
        eventEndsAt={currentEvent?.ends_at}
        eventId={currentEvent?.id}
        onLeave={handleLeaveBooth}
        onEndBooth={() => endBooth(currentBooth.id)}
      />
    );
  }

  // Handle location filter selection
  const handleLocationSelect = useCallback((location: LocationPickerValue) => {
    console.log("📍 [EXHIBITION] Location filter selected:", location);
    setLocationFilter({
      countryCode: location.countryCode,
      stateId: location.stateId,
      cityId: location.cityId,
    });
    setShowLocationFilter(false);
    
    // If a specific country is selected, navigate to it
    if (location.countryCode && (screen as string) === "countries") {
      const country = countries.find(c => c.code === location.countryCode);
      if (country) {
        handleSelectCountry(country);
      }
    }
  }, [countries, screen]);

  return (
    <>
      {/* Admin Controls - visible on all screens for admins */}
      <ExhibitionAdminControls
        eventId={eventData?.id ?? null}
        eventState={eventState}
        isAdminOverride={isAdminOverride}
        isPaused={isPaused}
        debugInfo={debugInfo ?? null}
        onStateChange={refetchEventState}
      />
      
      {/* Location Filter Picker */}
      <CountryPicker
        isOpen={showLocationFilter}
        onClose={() => setShowLocationFilter(false)}
        selectedCountry={locationFilter.countryCode || "any"}
        onSelect={(countryCode) => {
          setLocationFilter({ countryCode });
          setShowLocationFilter(false);
        }}
        showStateCity={true}
        selectedState={locationFilter.stateId}
        selectedCity={locationFilter.cityId}
        onSelectLocation={handleLocationSelect}
      />
    </>
  );
};

export default LoveExhibition;
